import React, { useState, useEffect, useRef } from 'react';
import {
    Typography,
    Box,
    Chip,
    TextField,
    CircularProgress,
    Avatar,
    Popover,
    List,
    ListItem,
    ListItemText,
    Checkbox,
    InputAdornment,
    IconButton,
    Button
} from '@mui/material';
import { AcUnit, Search, Close, KeyboardArrowDown, DoneAll, DeleteSweep } from '@mui/icons-material';
import {
    usePopupState,
    bindTrigger,
    bindPopover,
} from 'material-ui-popup-state/hooks';
import { enqueueSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../store/store';
import { useCardFreezeMutation, useFetchDataNoCacheQuery } from '../../../services/services';
import { encrypt } from '../../../../../utils/encrypt';
import { ConfirmationModal } from '../../../../../components/ConfirmationModal';

interface CardFreezeResponse {
    success: boolean;
    message: string;
}

interface FreezeCardActionProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

const FreezeCardAction: React.FC<FreezeCardActionProps> = ({ isOpen, onClose, onSuccess }) => {
    const { currentUserState } = useSelector((state: RootState) => state.currentUserState);
    const [freezeSubmitLoading, setFreezeSubmitLoading] = useState(false);
    const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
    const [selectedCards, setSelectedCards] = useState<any[]>([]);
    const [cardSearchText, setCardSearchText] = useState('');

    const toggleCard = (card: any) => {
        const isSelected = selectedCards.some(c => c.cardNo === card.cardNo);
        if (isSelected) {
            setSelectedCards(prev => prev.filter(c => c.cardNo !== card.cardNo));
        } else {
            setSelectedCards(prev => [...prev, card]);
        }
    };

    const selectAllCards = () => {
        const newSelected = [...selectedCards];
        filteredCards.forEach(card => {
            if (!newSelected.some(c => c.cardNo === card.cardNo)) {
                newSelected.push(card);
            }
        });
        setSelectedCards(newSelected);
    };

    const clearAllCards = () => {
        setSelectedCards([]);
    };

    const isCardSelected = (cardNo: string) => {
        return selectedCards.some(c => c.cardNo === cardNo);
    };

    const [currentPage, setCurrentPage] = useState(1);
    const [allCards, setAllCards] = useState<any[]>([]);
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const isLoadingMoreRef = useRef<boolean>(false);
    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const listUrl = `/Card/CustomerCardsV2?customerId=${currentUserState?.companyId}`;
    // Yalnızca aktif durumdaki kartlar (A = Açık Kart)
    const cardFilters = [
        { field: "status", logicOperator: "and", values: ["A"], valueType: 1, operationType: 6 }
    ];
    const [requestBody, setRequestBody] = useState({
        filters: cardFilters,
        pageSize: 25,
        page: 1,
        orderBy: "",
        orderDesc: false,
        customerId: currentUserState?.companyId || undefined,
    });

    const popupState = usePopupState({
        variant: 'popover',
        popupId: 'card-freeze-popup',
    });

    useEffect(() => {
        if (currentUserState?.companyId) {
            setRequestBody(prev => ({ ...prev, customerId: currentUserState.companyId, filters: cardFilters }));
        }
    }, [currentUserState?.companyId]);

    const { data: fetchData, isLoading: isFetchLoading, isFetching } = useFetchDataNoCacheQuery(
        { url: listUrl, filter: requestBody, method: "POST" },
        { skip: !requestBody?.customerId || !isOpen, refetchOnMountOrArgChange: true }
    );
    const isRequestLoading = isFetching || isFetchLoading;
    const [cardFreeze] = useCardFreezeMutation();

    useEffect(() => {
        if (isOpen && currentUserState?.companyId) {
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
                scrollTimeoutRef.current = null;
            }
            setCurrentPage(1);
            setAllCards([]);
            setHasMore(true);
            setSelectedCards([]);
            setCardSearchText('');
            setIsLoadingMore(false);
            isLoadingMoreRef.current = false;
            setRequestBody(prev => ({ ...prev, page: 1, customerId: currentUserState?.companyId }));
        }
    }, [isOpen, currentUserState?.companyId]);

    useEffect(() => {
        if (requestBody?.page === 1 && isOpen) {
            setAllCards([]);
        }
    }, [requestBody?.page, isOpen]);

    useEffect(() => {
        if (fetchData && isOpen) {
            if (!fetchData.success || fetchData.data == null) {
                setHasMore(false);
                setIsLoadingMore(false);
                isLoadingMoreRef.current = false;
                if (currentPage === 1) setAllCards([]);
                return;
            }
            let newCards: any[] = Array.isArray(fetchData.data) ? fetchData.data : (fetchData.data?.data && Array.isArray(fetchData.data.data) ? fetchData.data.data : []);
            if (newCards.length > 0) {
                if (currentPage === 1) {
                    setAllCards(newCards);
                } else {
                    setAllCards(prev => {
                        const existingIds = new Set(prev.map((c: any) => c.id || c.cardNo));
                        const unique = newCards.filter((c: any) => !existingIds.has(c.id || c.cardNo));
                        return [...prev, ...unique];
                    });
                }
                setHasMore(newCards.length >= 25);
            } else {
                if (currentPage === 1) setAllCards([]);
                setHasMore(false);
            }
            setIsLoadingMore(false);
            isLoadingMoreRef.current = false;
        }
    }, [fetchData, currentPage, isOpen, isFetchLoading]);

    const filteredCards = allCards.filter((card: any) => !!card?.cardNo);

    const loadNextPage = () => {
        if (!hasMore || isLoadingMore || isRequestLoading || isLoadingMoreRef.current) return;
        isLoadingMoreRef.current = true;
        setIsLoadingMore(true);
        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);
        setRequestBody(prev => ({ ...prev, page: nextPage }));
    };

    // Arama metni değiştiğinde backend'e filtre göndererek server-side arama yap
    useEffect(() => {
        if (!isOpen) return;

        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current);
        }

        searchTimerRef.current = setTimeout(() => {
            const extraFilters = cardSearchText.trim()
                ? [{
                    field: "cardNo",
                    logicOperator: "and",
                    values: [cardSearchText.trim()],
                    valueType: 1,
                    operationType: 1
                }]
                : [];

            setAllCards([]);
            setCurrentPage(1);
            setHasMore(true);
            setIsLoadingMore(false);
            isLoadingMoreRef.current = false;
            setRequestBody(prev => ({
                ...prev,
                page: 1,
                filters: [...cardFilters, ...extraFilters]
            }));
        }, 500);

        return () => {
            if (searchTimerRef.current) {
                clearTimeout(searchTimerRef.current);
            }
        };
    }, [cardSearchText]);

    const handleRequestSubmit = () => {
        if (!selectedCards?.length) {
            enqueueSnackbar('Lütfen kart seçiniz', { variant: 'warning' });
            return;
        }
        setShowSubmitConfirm(true);
    };

    const performSubmit = async () => {
        if (!selectedCards?.length) {
            return;
        }
        try {
            setFreezeSubmitLoading(true);
            const cardInfo = {
                CardInfo: selectedCards.map(card => ({ CardNo: encrypt(card.cardNo) }))
            };
            const result = await cardFreeze(cardInfo);
            const response: CardFreezeResponse | undefined = (result as any).data;
            if (response?.success) {
                enqueueSnackbar(response.message, { variant: 'success' });
                onSuccess?.();
                handleClose();
            }
        } catch (error: any) {
            const errorMessage = error?.error?.message || error?.message || 'Bilinmeyen bir hata oluştu';
            enqueueSnackbar(errorMessage, { variant: 'error' });
        } finally {
            setFreezeSubmitLoading(false);
        }
    };

    const handleClose = () => {
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
            scrollTimeoutRef.current = null;
        }
        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current);
            searchTimerRef.current = null;
        }
        setSelectedCards([]);
        setCardSearchText('');
        setCurrentPage(1);
        setAllCards([]);
        setHasMore(true);
        setIsLoadingMore(false);
        isLoadingMoreRef.current = false;
        setRequestBody(prev => ({ ...prev, page: 1 }));
        setShowSubmitConfirm(false);
        onClose();
    };

    const submitConfirmContent =
        selectedCards.length === 1
            ? 'Seçilen kartı dondurmak istediğinize emin misiniz?'
            : `Seçilen ${selectedCards.length} adet kartı dondurmak istediğinize emin misiniz?`;

    return (
        <>
        <ConfirmationModal
            isOpen={isOpen}
            onClose={handleClose}
            onConfirm={handleRequestSubmit}
            title="Kartı Dondur"
            isLoading={false}
            confirmButtonText="Kaydet"
            closeButtonText="Vazgeç"
            optionalWidth="600px"
            customHeaderStyle={{
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                titleColor: 'white',
                icon: (
                    <Avatar sx={{ background: 'rgba(255,255,255,0.2)', color: 'white', width: 32, height: 32 }}>
                        <AcUnit fontSize="small" />
                    </Avatar>
                )
            }}
        >
            <Typography variant="body1" sx={{ mb: 3, color: '#64748b' }}>
                Dondurmak istediğiniz kartları seçiniz:
            </Typography>

            <Box
                {...bindTrigger(popupState)}
                sx={{
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    minHeight: '56px',
                    maxHeight: '120px',
                    overflowY: 'auto',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    cursor: 'pointer',
                    '&:hover': { borderColor: 'primary.main' },
                    position: 'relative',
                    pr: 5,
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': { background: '#ccc', borderRadius: '3px' },
                    '&::-webkit-scrollbar-thumb:hover': { background: '#aaa' }
                }}
            >
                {selectedCards.length === 0 ? (
                    <Typography sx={{ color: 'text.secondary', alignSelf: 'center' }}>
                        Kart arayın veya seçin
                    </Typography>
                ) : (
                    selectedCards.map((card) => (
                        <Chip
                            key={card.cardNo}
                            variant="outlined"
                            size="small"
                            color="primary"
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{card.cardNo}</Typography>
                                    {(card.embossName || card.cardName || card.customerName) && (
                                        <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                                            - {card.embossName || card.cardName || card.customerName}
                                        </Typography>
                                    )}
                                </Box>
                            }
                            onDelete={(e) => { e.stopPropagation(); toggleCard(card); }}
                            sx={{
                                '& .MuiChip-deleteIcon': {
                                    fontSize: '18px',
                                    '&:hover': { color: '#ffffff !important', backgroundColor: '#d32f2f !important', borderRadius: '50%', transform: 'scale(1.1)' }
                                }
                            }}
                        />
                    ))
                )}
                <Box sx={{ position: 'absolute', right: 8, top: '18px', display: 'flex', gap: 0.5 }}>
                    {isRequestLoading && currentPage === 1 && <CircularProgress size={20} color="inherit" />}
                    <KeyboardArrowDown sx={{ color: 'text.secondary' }} />
                </Box>
            </Box>

            <Popover
                {...bindPopover(popupState)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                    sx: { width: 'min(500px, calc(100vw - 16px))', mt: 1, maxHeight: 'min(450px, calc(100dvh - 32px))', overflow: 'hidden', display: 'flex', flexDirection: 'column' }
                }}
            >
                <>
                    <Box sx={{ p: 2, borderBottom: '1px solid #eee', flexShrink: 0 }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Kart No ile ara..."
                            value={cardSearchText}
                            onChange={(e) => setCardSearchText(e.target.value)}
                            autoFocus
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start"><Search fontSize="small" color="action" /></InputAdornment>
                                ),
                                endAdornment: cardSearchText && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setCardSearchText('')}><Close fontSize="small" /></IconButton>
                                    </InputAdornment>
                                )
                            }}
                            sx={{ mb: 1.5 }}
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button size="small" variant="outlined" startIcon={<DoneAll />} onClick={selectAllCards} fullWidth sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                                Tümünü Seç
                            </Button>
                            <Button size="small" variant="outlined" color="error" startIcon={<DeleteSweep />} onClick={clearAllCards} fullWidth sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                                Tümünü Kaldır
                            </Button>
                        </Box>
                    </Box>
                    <List
                        sx={{
                            flex: '1 1 auto',
                            minHeight: 0,
                            overflowY: 'auto',
                            maxHeight: 'none',
                            '&::-webkit-scrollbar': { width: '8px' },
                            '&::-webkit-scrollbar-track': { background: '#f1f1f1' },
                            '&::-webkit-scrollbar-thumb': { background: '#ccc', borderRadius: '4px' },
                            '&::-webkit-scrollbar-thumb:hover': { background: '#aaa' }
                        }}
                        onScroll={(e) => {
                            const target = e.currentTarget;
                            if (isRequestLoading || isLoadingMore || !hasMore || isLoadingMoreRef.current) return;
                            if (target.scrollHeight - target.scrollTop <= target.clientHeight + 30) loadNextPage();
                        }}
                    >
                        {isRequestLoading && currentPage === 1 ? (
                            <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                                <CircularProgress size={40} thickness={4} sx={{ color: '#f59e0b' }} />
                                <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>Kartlar Yükleniyor...</Typography>
                            </Box>
                        ) : filteredCards.length === 0 ? (
                            <Box sx={{ p: 2, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">Kart bulunamadı</Typography>
                            </Box>
                        ) : (
                            filteredCards.map((card) => (
                                <ListItem key={card.cardNo} button onClick={() => toggleCard(card)} sx={{ py: 0.5 }}>
                                    <Checkbox edge="start" checked={isCardSelected(card.cardNo)} tabIndex={-1} disableRipple />
                                    <ListItemText
                                        primary={card.cardNo}
                                        secondary={card.cardName || card.embossName || card.customerName}
                                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                        secondaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.8rem' } }}
                                    />
                                    <Typography variant="caption" sx={{ color: 'text.secondary', ml: 'auto', pl: 2, fontWeight: 600, fontSize: '0.85rem' }}>
                                        {card.customerName || card.embossName || card.cardName}
                                    </Typography>
                                </ListItem>
                            ))
                        )}
                        {(isLoadingMore || isLoadingMoreRef.current || (isRequestLoading && currentPage > 1)) && hasMore && (
                            <ListItem sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', py: 2, background: 'linear-gradient(to bottom, transparent, rgba(245, 158, 11, 0.05))', borderTop: '1px solid #f1f5f9', width: '100%' }}>
                                <Box sx={{ position: 'relative', display: 'inline-flex', mb: 1 }}>
                                    <CircularProgress size={24} thickness={4} sx={{ color: '#e2e8f0' }} variant="determinate" value={100} />
                                    <CircularProgress size={24} thickness={4} sx={{ color: '#f59e0b', position: 'absolute', left: 0, animationDuration: '800ms' }} />
                                </Box>
                                <Typography variant="caption" sx={{ color: '#f59e0b', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                                    Kartlar Yükleniyor
                                </Typography>
                            </ListItem>
                        )}
                    </List>
                </>
            </Popover>
        </ConfirmationModal>
        <ConfirmationModal
            isOpen={showSubmitConfirm}
            onClose={() => setShowSubmitConfirm(false)}
            onConfirm={() => void performSubmit()}
            title="İşlem Onayı"
            content={submitConfirmContent}
            confirmButtonText={freezeSubmitLoading ? 'İşleniyor...' : 'Onayla'}
            closeButtonText="İptal"
            isLoading={freezeSubmitLoading}
            optionalWidth="440px"
        />
        </>
    );
};

export default FreezeCardAction;
